import React, { useState, useEffect } from 'react';
import { Star, StarHalf } from 'lucide-react';
import { getProfileConfig, Skill } from '../config/profile';
import SkillIcon from './SkillIcon';

const Skills: React.FC = () => {
  const [selectedTag, setSelectedTag] = useState('all');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getProfileConfig();
        setSkills(config.skills);
        setAllTags(config.skillTags);
      } catch (error) {
        console.error('Failed to load profile config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Filter skills based on selected tag
  const filteredSkills = selectedTag === 'all' 
    ? skills 
    : skills.filter(skill => skill.tags.includes(selectedTag));

  // Render stars based on rating
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="w-5 h-5 text-yellow-500 fill-yellow-500" />);
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="w-5 h-5 text-yellow-500 fill-yellow-500" />);
    }
    
    // Add empty stars
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-5 h-5 text-gray-300" />);
    }
    
    return stars;
  };

  if (loading) {
    return (
      <div id="skills" className="py-10 bg-white dark:bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading skills...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="skills" className="py-10 bg-white dark:bg-dark-900">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-8">Skills</h2>
        
        {/* Tags filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${selectedTag === tag 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
            >
              {tag.charAt(0).toUpperCase() + tag.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Skills grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              {/* Skill header with icon and name */}
              <div className="flex items-center gap-3 mb-3">
                {skill.icon && (
                  <SkillIcon 
                    iconName={skill.icon} 
                    className="text-gray-700" 
                    size={32}
                  />
                )}
                <h3 className="text-xl font-bold text-blue-700">{skill.name}</h3>
              </div>
              
              {/* Rating stars */}
              <div className="flex items-center mb-3">
                {renderStars(skill.rating)}
              </div>
              
              {/* Filter tags */}
              <div className="flex flex-wrap gap-1 mb-3">
                {skill.tags.map(tag => (
                  <span 
                    key={tag} 
                    className="px-2 py-1 bg-blue-100 text-xs rounded-full text-blue-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              {/* Category tags - technologies/patterns */}
              <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-200">
                {/* <p className="text-sm text-gray-500 w-full mb-2">Technologies & Patterns:</p> */}
                {skill.categoryTags.map(tag => (
                  <span 
                    key={tag} 
                    className="px-2 py-1 bg-gray-200 text-xs rounded-md text-gray-700 font-semibold"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Skills;